import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { SignalDecision, SignalDetailDto, SignalDto, SignalSummary } from '@intelimove/contracts';
import { Modal } from '../components/Modal';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { FilterBar, PageHead, StatusPill } from '../components/Widgets';
import {
  AVAILABILITY_LABEL, CATEGORY_LABEL, NATURE_LABEL, SIGNAL_STATUS_LABEL, SOURCE_CLASSIFICATION_LABEL, SOURCE_ORIGIN_LABEL, TREATMENT_LABEL,
  VALIDATION_LABEL, relativeTime, signalsAdapter,
} from '../data/adapters';
import { useResource } from '../hooks/useResource';

const fmt = new Intl.NumberFormat('pt-BR');
const dateFmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const dateTimeFmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const day = (iso: string | null) => (iso ? dateFmt.format(new Date(iso)) : null);
const dayTime = (iso: string | null) => (iso ? dateTimeFmt.format(new Date(iso)) : '—');

const TIMING_LABEL: Record<string, string> = {
  ABORDAR_AGORA: 'Abordar agora', ACOMPANHAR_DE_PERTO: 'Acompanhar de perto', AGUARDAR_CONFIRMACAO: 'Aguardar confirmação',
  SEM_MOMENTO_CLARO: 'Sem momento claro', SINAL_SUPERADO: 'Sinal superado',
};
const CONFIDENCE_LABEL: Record<string, string> = { ALTA: 'alta', MEDIA: 'média', BAIXA: 'baixa' };
const VALIDATION_TONE: Record<string, string> = { CONFIRMADO: 'mint', REJEITADO: 'gray', EVIDENCIA_INSUFICIENTE: 'critical', PENDENTE: 'default' };
const KIND_LABEL: Record<string, string> = { demo_fixture: 'Demonstração', rss: 'Feed RSS', atom: 'Feed Atom', publications_page: 'Página de publicações', page: 'Página monitorada', youtube: 'Canal do YouTube', social: 'Rede social' };
const VERIFICATION_TEXT: Record<string, string> = {
  SEM_EVIDENCIA_VERIFICAVEL: 'Sem evidência verificável', ORIGEM_DEMONSTRATIVA: 'Conteúdo demonstrativo', ORIGEM_NAO_COMPROVADA: 'Origem não comprovada',
};
const DECISION_LABEL: Record<string, string> = {
  CONFIRMAR: 'Confirmou', ACOMPANHAR: 'Passou a acompanhar', REJEITAR: 'Rejeitou', VISTO: 'Marcou como visto', MARCAR_SUPERADO: 'Marcou como resolvido',
  REAVALIAR: 'Pediu reavaliação', CONVERTER_EMPRESA_ALVO: 'Vinculou à empresa-alvo',
};

// IM-EXT-011: o que cada decisão significa, exibido junto dos botões (title + texto de apoio no detalhe).
export const DECISION_HELP: Record<'CONFIRMAR' | 'ACOMPANHAR' | 'REJEITAR', string> = {
  CONFIRMAR: 'Você conferiu a publicação original e valida o sinal como verdadeiro e relevante.',
  ACOMPANHAR: 'Inicia ou mantém o tratamento contínuo deste sinal; não valida o sinal.',
  REJEITAR: 'O sinal deixa de alimentar a inteligência ativa: recomendações, mapa comercial e indicadores.',
};
const ACTIONS: Array<['CONFIRMAR' | 'ACOMPANHAR' | 'REJEITAR', string]> = [['CONFIRMAR', 'Confirmar'], ['ACOMPANHAR', 'Acompanhar'], ['REJEITAR', 'Rejeitar']];

function availableActions(s: SignalDto) {
  return ACTIONS.filter(([d]) => !(d === 'CONFIRMAR' && (s.validationStatus === 'CONFIRMADO' || s.verification !== 'VERIFICAVEL'))
    && !(d === 'ACOMPANHAR' && s.treatmentStatus === 'ACOMPANHANDO') && !(d === 'REJEITAR' && s.validationStatus === 'REJEITADO'));
}

function evidenceText(s: SignalDto): string {
  if (s.verification !== 'VERIFICAVEL') return VERIFICATION_TEXT[s.verification] ?? 'Sem evidência verificável';
  if (s.publication.availability === 'INDISPONIVEL') return 'Publicação indisponível na última verificação';
  return `${NATURE_LABEL[s.nature] ?? s.nature} com publicação original`;
}

/** Link seguro para a publicação original: nova aba, sem acesso à janela de origem, com nome acessível. */
export function OriginalLink({ s, className = 'cta original-link' }: { s: SignalDto; className?: string }) {
  if (!s.publication.url) return null;
  return (
    <a className={className} href={s.publication.url} target="_blank" rel="noopener noreferrer"
      aria-label={`Abrir publicação original: ${s.publication.originalTitle} (${s.publication.domain ?? 'site externo'}, abre em nova aba)`}>
      <span aria-hidden="true">↗</span> Abrir publicação original
    </a>
  );
}

/** Sinal com fonte e publicação original visíveis (IM-EXT-011) e decisão humana (kernel do Ceres, IM-EXT-001). */
export function SignalCard({ s, onDecide, onDetails }: { s: SignalDto; onDecide?: (s: SignalDto, d: SignalDecision) => void; onDetails?: (s: SignalDto) => void }) {
  const published = day(s.publication.publishedAt);
  const verifiable = s.verification === 'VERIFICAVEL';
  return (
    <article className={`signal-card${verifiable ? '' : ' unverified'}`} aria-label={s.title}>
      <div className="signal-card-top">
        <span className={`tag ${s.score >= 85 ? '' : 'mint'}`}>{CATEGORY_LABEL[s.category] ?? s.category}</span>
        <StatusPill tone={VALIDATION_TONE[s.validationStatus] ?? 'default'}>{VALIDATION_LABEL[s.validationStatus] ?? s.validationStatus}</StatusPill>
        {s.treatmentStatus !== 'NOVO' && <StatusPill tone="amber">{TREATMENT_LABEL[s.treatmentStatus] ?? s.treatmentStatus}</StatusPill>}
      </div>
      <h3>{s.title}</h3>
      <p>{s.summary}</p>
      <dl className="signal-meta">
        <dt>Empresa</dt><dd>{s.accountName}</dd>
        <dt>Fonte</dt><dd>{s.source.name}{s.publication.domain ? ` · ${s.publication.domain}` : ''}</dd>
        <dt>Publicação</dt><dd>{published ? `${published} · ${relativeTime(s.publication.publishedAt!)}` : `sem data declarada · coletada ${relativeTime(s.detectedAt)}`}</dd>
        <dt>Evidência</dt><dd className={verifiable ? '' : 'warn'}>{evidenceText(s)}</dd>
      </dl>
      {s.reading?.timing && <p className="signal-timing">{TIMING_LABEL[s.reading.timing] ?? s.reading.timing}{s.reading.confidence ? ` · confiança ${CONFIDENCE_LABEL[s.reading.confidence]}` : ''}</p>}
      <div className="source"><span>{s.scoreBreakdown ? 'Pontuação explicada nos detalhes' : 'Pontuação sem composição registrada'}</span><b>{s.score} pontos</b></div>
      <div className="signal-links">
        {verifiable ? <OriginalLink s={s} /> : <span className="muted small">Sem publicação original verificável</span>}
        {onDetails && <button type="button" className="small-action" onClick={() => onDetails(s)} aria-label={`Ver detalhes: ${s.title}`}>Ver detalhes</button>}
      </div>
      {onDecide && (
        <div className="signal-actions" role="group" aria-label={`Decidir sobre: ${s.title}`}>
          {availableActions(s).map(([d, label]) => <button key={d} type="button" className="chip" title={DECISION_HELP[d]} onClick={() => onDecide(s, d)}>{label}</button>)}
        </div>
      )}
    </article>
  );
}

function SignalDetail({ id, onClose, onDecided }: { id: string; onClose: () => void; onDecided: (s: SignalDto) => void }) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { state, reload } = useResource((sig) => signalsAdapter.get(id, sig), [id]);
  const decide = async (g: SignalDetailDto, d: SignalDecision) => {
    setBusy(true);
    try {
      const r = await signalsAdapter.decide(g.id, d, note.trim() || undefined);
      toast(d === 'CONVERTER_EMPRESA_ALVO' ? `sinal vinculado à empresa-alvo ${g.accountName}.` : `decisão registrada: ${VALIDATION_LABEL[r.validationStatus]?.toLowerCase()} · ${TREATMENT_LABEL[r.treatmentStatus]?.toLowerCase()}.`);
      setNote('');
      onDecided({ ...g, status: r.status, validationStatus: r.validationStatus, treatmentStatus: r.treatmentStatus });
      reload();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };
  const check = async (g: SignalDetailDto) => {
    setBusy(true);
    try {
      const r = await signalsAdapter.checkPublication(g.id);
      toast(r.detail);
      reload();
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Detalhes do sinal" onClose={onClose} wide>
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(g) => {
          const b = g.scoreBreakdown;
          const sameTitle = g.title.trim() === g.publication.originalTitle.trim();
          return (
            <div className="signal-detail">
              <section aria-labelledby="sd-sintese">
                <h3 id="sd-sintese" className="eyebrow-label">Título do sinal (síntese InteliMove)</h3>
                <p className="sd-title">{g.title}</p>
                <p className="muted small">{g.summary}</p>
                <p><StatusPill tone={VALIDATION_TONE[g.validationStatus] ?? 'default'}>{VALIDATION_LABEL[g.validationStatus]}</StatusPill>{' '}
                  <StatusPill tone="amber">{TREATMENT_LABEL[g.treatmentStatus]}</StatusPill>{' '}
                  <span className="muted small">Estado legado: {SIGNAL_STATUS_LABEL[g.status] ?? g.status}</span></p>
              </section>

              <section aria-labelledby="sd-pub">
                <h3 id="sd-pub" className="eyebrow-label">Publicação original</h3>
                {g.verification !== 'VERIFICAVEL' && (
                  <p className="notice-inline" role="note"><b>{VERIFICATION_TEXT[g.verification] ?? 'Sem evidência verificável'}.</b> Este sinal não é tratado como fato:
                    não pode ser confirmado, não chega à alta prioridade e não alimenta recomendações, mapa comercial ou indicadores. Investigue a origem ou rejeite.</p>
                )}
                <dl className="sd-grid">
                  <dt>Título original</dt><dd>{g.publication.originalTitle}{sameTitle && <span className="muted small"> (o título do sinal reproduz o da publicação)</span>}</dd>
                  <dt>Veículo / fonte</dt><dd>{g.source.name}</dd>
                  <dt>Domínio</dt><dd>{g.publication.domain ?? '—'}</dd>
                  <dt>URL original</dt><dd className="break">{g.publication.url ?? 'Não há URL verificável para esta publicação.'}</dd>
                  <dt>Publicada em</dt><dd>{day(g.publication.publishedAt) ?? 'sem data declarada pela fonte'}</dd>
                  <dt>Coletada em</dt><dd>{dayTime(g.publication.fetchedAt)}</dd>
                  <dt>Disponibilidade</dt><dd>{AVAILABILITY_LABEL[g.publication.availability]}{g.publication.checkedAt && ` · ${dayTime(g.publication.checkedAt)}`}
                    {g.publication.availabilityDetail && <span className="muted small"> — {g.publication.availabilityDetail}</span>}</dd>
                </dl>
                <div className="signal-links">
                  <OriginalLink s={g} />
                  {g.publication.url && <button type="button" className="small-action" disabled={busy} onClick={() => check(g)}>Verificar disponibilidade agora</button>}
                </div>
              </section>

              <section aria-labelledby="sd-ev">
                <h3 id="sd-ev" className="eyebrow-label">Trecho que sustenta o sinal</h3>
                {g.evidenceDetail.excerpt ? <blockquote className="sd-quote">{g.evidenceDetail.excerpt}</blockquote> : <p className="muted">Sem trecho coletado.</p>}
                <dl className="sd-grid">
                  <dt>Natureza</dt><dd>{NATURE_LABEL[g.nature] ?? g.nature}{g.reading?.conclusionNature ? ` · leitura: ${g.reading.conclusionNature === 'DEDUCAO_SOBRE_FATO' ? 'dedução sobre fato' : g.reading.conclusionNature === 'DEDUCAO_SOBRE_INDICIO' ? 'dedução sobre indício' : 'hipótese'}` : ''}</dd>
                  <dt>Confiança</dt><dd>{g.reading?.confidence ? CONFIDENCE_LABEL[g.reading.confidence] : 'não avaliada'}</dd>
                  {g.reading?.timing && <><dt>Momento</dt><dd>{TIMING_LABEL[g.reading.timing] ?? g.reading.timing}</dd></>}
                </dl>
              </section>

              <section aria-labelledby="sd-score">
                <h3 id="sd-score" className="eyebrow-label">Pontuação: {g.score} pontos</h3>
                {b ? (
                  <>
                    <table className="data-table sd-score">
                      <caption className="sr-only">Composição da pontuação</caption>
                      <thead><tr><th scope="col">Critério</th><th scope="col">Pontos</th><th scope="col">Por quê</th></tr></thead>
                      <tbody>{b.components.map((c) => <tr key={c.key}><th scope="row">{c.label}</th><td>{c.points}/{c.max}</td><td>{c.reason}</td></tr>)}</tbody>
                    </table>
                    <p className="muted small">Soma dos critérios: {b.raw}. {b.caps.length ? `Travas aplicadas: ${b.caps.map((c) => `${c.reason} (máximo ${c.limit})`).join(' ')}` : 'Nenhuma trava aplicada.'}
                      {' '}{b.highPriorityEligible ? 'Elegível para alta prioridade.' : 'Não elegível para alta prioridade.'}</p>
                  </>
                ) : <p className="muted">Pontuação gravada antes da composição explicada; não há como detalhá-la.</p>}
              </section>

              <section aria-labelledby="sd-rel">
                <h3 id="sd-rel" className="eyebrow-label">Por que é relevante</h3>
                <p>{g.reading?.commercialReason ?? 'Sem leitura comercial registrada.'}</p>
                {b && <><p className="muted small">Critérios atendidos:</p><ul className="sd-list">{b.components.filter((c) => c.points > 0).map((c) => <li key={c.key}>{c.label}: {c.reason}</li>)}</ul></>}
                {(g.reading?.toConfirm.length ?? 0) > 0 && <><p className="muted small">A confirmar:</p><ul className="sd-list">{g.reading!.toConfirm.map((x) => <li key={x}>{x}</li>)}</ul></>}
              </section>

              <section aria-labelledby="sd-src">
                <h3 id="sd-src" className="eyebrow-label">Fonte e origem do monitoramento</h3>
                <dl className="sd-grid">
                  <dt>Fonte</dt><dd>{g.source.name} · {KIND_LABEL[g.source.kind] ?? g.source.kind}</dd>
                  <dt>Classificação</dt><dd>{SOURCE_CLASSIFICATION_LABEL[g.source.classification] ?? g.source.classification}</dd>
                  <dt>Por que foi coletado</dt><dd>{g.source.origin ? SOURCE_ORIGIN_LABEL[g.source.origin] : 'Origem do monitoramento não registrada (fonte anterior à rastreabilidade)'}
                    {g.source.accountName && ` · empresa monitorada: ${g.source.accountName}`}</dd>
                </dl>
                <div className="signal-links">
                  {g.source.url && <a className="small-action" href={g.source.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir fonte monitorada: ${g.source.name} (abre em nova aba)`}>Abrir fonte monitorada</a>}
                  <Link className="small-action" to={`/fontes?fonte=${g.source.id}`}>Abrir evidências da fonte</Link>
                </div>
              </section>

              <section aria-labelledby="sd-emp">
                <h3 id="sd-emp" className="eyebrow-label">Empresa relacionada: {g.accountName}</h3>
                <div className="signal-links">
                  {g.relatedTarget
                    ? <Link className="small-action" to={`/empresas-alvo/${g.relatedTarget.id}`}>Abrir empresa-alvo</Link>
                    : <Link className="small-action" to={`/empresas-alvo?incluir=${encodeURIComponent(g.accountName)}`}>Pesquisar e adicionar como empresa-alvo</Link>}
                  {g.relatedTarget && g.verification === 'VERIFICAVEL' && g.treatmentStatus !== 'CONVERTIDO_EMPRESA_ALVO' && (
                    <button type="button" className="small-action" disabled={busy} onClick={() => decide(g, 'CONVERTER_EMPRESA_ALVO')}>Vincular sinal à empresa-alvo</button>)}
                </div>
              </section>

              <section aria-labelledby="sd-dec">
                <h3 id="sd-dec" className="eyebrow-label">Decidir</h3>
                <ul className="sd-list">{ACTIONS.map(([d, label]) => <li key={d}><b>{label}:</b> {DECISION_HELP[d]}</li>)}</ul>
                <div className="field"><label htmlFor="sd-note">Motivo (opcional)</label><textarea id="sd-note" rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} /></div>
                <div className="signal-actions" role="group" aria-label="Decisões sobre o sinal">
                  {availableActions(g).map(([d, label]) => <button key={d} type="button" className="chip" disabled={busy} title={DECISION_HELP[d]} onClick={() => decide(g, d)}>{label}</button>)}
                </div>
              </section>

              <section aria-labelledby="sd-hist">
                <h3 id="sd-hist" className="eyebrow-label">Histórico de decisões</h3>
                {g.decisions.length === 0 ? <p className="muted">Nenhuma decisão registrada.</p> : (
                  <ul className="sd-list">{g.decisions.map((d) => (
                    <li key={d.id}>{dayTime(d.createdAt)} · {d.decidedBy ?? 'usuário removido'} · {DECISION_LABEL[d.decision] ?? d.decision}
                      {d.toValidation && ` · validação: ${VALIDATION_LABEL[d.fromValidation ?? ''] ?? '—'} → ${VALIDATION_LABEL[d.toValidation]}`}
                      {d.toTreatment && ` · tratamento: ${TREATMENT_LABEL[d.fromTreatment ?? ''] ?? '—'} → ${TREATMENT_LABEL[d.toTreatment]}`}
                      {d.note && <span className="muted"> — “{d.note}”</span>}</li>))}</ul>
                )}
              </section>
            </div>
          );
        }}
      </AsyncView>
    </Modal>
  );
}

export function Signals() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const busca = params.get('busca')?.toLowerCase() ?? '';
  const [evidence, setEvidence] = useState<'verificavel' | 'sem_evidencia'>('verificavel');
  const [category, setCategory] = useState<string>('todos');
  const [status, setStatus] = useState<string>('ativos');
  const [decided, setDecided] = useState<Record<string, Partial<SignalDto>>>({});
  const [summary, setSummary] = useState<SignalSummary | null>(null);
  const [extra, setExtra] = useState<SignalDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const detailId = params.get('sinal');
  const openDetail = (s: SignalDto) => setParams((p) => { p.set('sinal', s.id); return p; });
  const closeDetail = () => setParams((p) => { p.delete('sinal'); return p; });

  useEffect(() => { signalsAdapter.summary().then(setSummary).catch(() => setSummary(null)); }, []);
  const cat = category === 'todos' || category === 'alta' ? null : category;
  const { state, reload } = useResource(
    (s) => signalsAdapter.list({ limit: 12, category: cat, status: status === 'todos' ? null : status, evidence }, s).then((p) => { setExtra([]); setDecided({}); setCursor(p.nextCursor); return p; }),
    [cat, status, evidence], (p) => p.items.length === 0,
  );

  const more = async () => {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const p = await signalsAdapter.list({ limit: 12, category: cat, cursor, status: status === 'todos' ? null : status, evidence });
      setExtra((e) => [...e, ...p.items]);
      setCursor(p.nextCursor);
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const decide = async (sig: SignalDto, d: SignalDecision) => {
    try {
      const r = await signalsAdapter.decide(sig.id, d);
      setDecided((m) => ({ ...m, [sig.id]: { status: r.status, validationStatus: r.validationStatus, treatmentStatus: r.treatmentStatus } }));
      toast(`${sig.accountName}: ${VALIDATION_LABEL[r.validationStatus]?.toLowerCase()} · ${TREATMENT_LABEL[r.treatmentStatus]?.toLowerCase()}.`);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  const options = [
    { value: 'todos', label: `Todos · ${fmt.format(summary?.total ?? 0)}` },
    { value: 'alta', label: `Alta prioridade · ${summary?.highPriority ?? 0}` },
    ...Object.entries(summary?.byCategory ?? {}).map(([k, n]) => ({ value: k, label: `${CATEGORY_LABEL[k]} · ${n}` })),
  ];

  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Monitoramento contínuo" title="Sinais de mercado" subtitle="Acontecimentos públicos verificáveis, com a publicação original de cada sinal."
        actions={<><Link className="secondary" to="/configuracoes/contexto" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Revisar critérios</Link><Link className="cta" to="/fontes" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Adicionar monitoramento</Link></>} />
      <FilterBar label="Filtrar sinais por evidência" value={evidence} onChange={setEvidence} options={[
        { value: 'verificavel', label: `Com publicação verificável · ${fmt.format(summary?.total ?? 0)}` },
        { value: 'sem_evidencia', label: `Sem evidência verificável · ${fmt.format(summary?.withoutEvidence ?? 0)}` },
      ]} />
      {evidence === 'sem_evidencia' && <p className="notice-inline" role="note">Sinais sem publicação original verificável ficam separados: não são tratados como fato, não podem ser confirmados,
        não chegam à alta prioridade e não alimentam recomendações, mapa comercial nem indicadores. Investigue a origem ou rejeite.</p>}
      <FilterBar label="Filtrar sinais por decisão" value={status} onChange={setStatus} options={[
        { value: 'ativos', label: 'Ativos' }, { value: 'NOVO', label: 'Novos' }, { value: 'CONFIRMADO', label: 'Confirmados' },
        { value: 'ACOMPANHANDO', label: 'Acompanhando' }, { value: 'REJEITADO', label: 'Rejeitados' }, { value: 'todos', label: 'Todos' },
      ]} />
      {evidence === 'verificavel' && <FilterBar label="Filtrar sinais por categoria" options={options} value={category} onChange={setCategory} />}
      <div style={{ marginTop: 16 }}>
        <AsyncView state={state} onRetry={reload} loading="grid"
          empty={evidence === 'sem_evidencia'
            ? <EmptyState title="Nenhum sinal sem evidência verificável">Todos os sinais deste filtro têm publicação original verificável.</EmptyState>
            : <EmptyState title="Nenhum sinal verificável foi encontrado ainda." action={<div className="signal-links"><Link className="cta" to="/fontes" style={{ textDecoration: 'none' }}>Adicionar fonte</Link>
                <Link className="small-action" to="/monitoramento">Configurar monitoramento</Link><Link className="small-action" to="/configuracoes/contexto">Revisar critérios</Link></div>}>
                Um sinal só aparece aqui quando uma fonte real monitorada publica algo verificável sobre uma empresa. Adicione fontes das empresas que importam para
                você, configure o monitoramento de páginas e revise os critérios da sua empresa. A InteliMove não preenche esta tela com exemplos.</EmptyState>}>
          {(page) => {
            let items = [...page.items, ...extra].map((x) => (decided[x.id] ? { ...x, ...decided[x.id] } as SignalDto : x));
            if (category === 'alta') items = items.filter((s) => s.score >= 85);
            if (busca) items = items.filter((s) => `${s.accountName} ${s.title}`.toLowerCase().includes(busca));
            return (
              <>
                {busca && <p className="card-sub" style={{ marginBottom: 12 }}>Filtrando por “{params.get('busca')}” · <Link to="/sinais">limpar</Link></p>}
                {items.length === 0 ? <EmptyState title="Nenhum sinal para este filtro">Ajuste os filtros para ver outros sinais.</EmptyState> : (
                  <div className="signal-grid">{items.map((s) => <SignalCard key={s.id} s={s} onDecide={decide} onDetails={openDetail} />)}</div>
                )}
                {cursor && <div className="load-more"><button className="secondary" onClick={more} disabled={loadingMore}>{loadingMore ? 'Carregando…' : 'Carregar mais sinais'}</button></div>}
              </>
            );
          }}
        </AsyncView>
      </div>
      {detailId && /^[0-9a-f-]{36}$/.test(detailId) && <SignalDetail id={detailId} onClose={closeDetail} onDecided={(s) => setDecided((m) => ({ ...m, [s.id]: { status: s.status, validationStatus: s.validationStatus, treatmentStatus: s.treatmentStatus } }))} />}
    </section>
  );
}
